import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  Eye,
  EyeOff,
  Loader2,
  User,
  Lock,
  LogIn,
  Sparkles,
  Shield,
  PartyPopper,
} from "lucide-react";
import Modal from "Common/Components/Modal";
import img1 from "assets/images/logo/logo.png"; // مسیر لوگو را تنظیم کنید
import axios from "axios";
import { getRoleId } from "Common/Components/getRoleId";
import { getLandingRoute } from "Common/constants/roles";

// -------- افزودن فونت Vazir --------
const FontLoader: React.FC = () => {
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://cdn.jsdelivr.net/gh/rastikerdar/vazir-font@v30.1.0/dist/font-face.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  return null;
};

// -------- بخش برندینگ (سمت چپ) --------
const BrandSide: React.FC = () => {
  return (
    <div className="relative hidden lg:flex lg:w-1/2 flex-col items-center justify-center bg-red-500 p-12 text-white overflow-hidden">
      {/* دایره‌های تزئینی */}
      <div className="absolute top-[-10%] left-[-10%] h-[60%] w-[60%] rounded-full bg-white/10 blur-3xl" />
      <div className="absolute bottom-[-10%] right-[-10%] h-[50%] w-[50%] rounded-full bg-red-300/20 blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[80%] w-[80%] rounded-full bg-white/5 blur-2xl" />

      <div className="relative z-10 text-center max-w-md">
        <div className="mb-8 flex justify-center">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm shadow-xl">
            <img
              src={img1}
              alt="ماهان پانل"
              className="h-14 w-14 object-contain"
            />
          </div>
        </div>
        <h1
          className="text-5xl font-extrabold tracking-tight drop-shadow-lg"
          style={{ fontFamily: "Vazir, sans-serif" }}
        >
          <span className="text-black">ماهان</span>{" "}
          <span style={{ color: "#B4B4B4" }}>پانل</span>
        </h1>
        <p
          className="mt-4 text-xl font-light text-white/90 drop-shadow"
          style={{ fontFamily: "Vazir, sans-serif" }}
        >
          انتخاب اول در صنعت ساندویچ پانل
        </p>
        <div className="mt-8 h-1 w-20 mx-auto rounded-full bg-white/30" />
        <p
          className="mt-6 text-sm text-white/80"
          style={{ fontFamily: "Vazir, sans-serif" }}
        >
          سامانه هوشمند مدیریت یکپارچه
        </p>
        <div className="mt-10 flex justify-center gap-4 text-white/70">
          <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
            <Shield className="h-4 w-4" />
            <span style={{ fontFamily: "Vazir, sans-serif" }}>امنیت بالا</span>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
            <Sparkles className="h-4 w-4" />
            <span style={{ fontFamily: "Vazir, sans-serif" }}>پیشرفته</span>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-8 text-center text-sm text-white/60"
        style={{ fontFamily: "Vazir, sans-serif" }}
      >
        © ۱۴۰۴-۱۴۰۵ - تمامی حقوق محفوظ است
      </div>
    </div>
  );
};

// -------- فرم لاگین (سمت راست) --------
const LoginForm: React.FC = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("نام کاربری یا ایمیل ضروری است"),
      password: Yup.string().required("رمز عبور ضروری است"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          username: values.username,
          password: values.password,
          grant_type: 'password',
        });

        const response = await axios.post(
          'https://portal-api.mahanpanel.com/auth/token',
          params,
          {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          }
        );

        const { access_token, refresh_token } = response.data;
        // ذخیره توکن‌ها ...
        localStorage.setItem("app_at_mahan", access_token)
        localStorage.setItem("app_rt_mahan", refresh_token)
        setShowWelcome(true);

        // هر نقش فقط بخش‌های خودش را دارد، پس نمی‌شود همه را به یک صفحه‌ی ثابت فرستاد؛
        // مثلاً کارشناس حسابداری «پیش‌فاکتورها» را ندارد و آن مسیر برایش به صفحه‌ی
        // «یافت نشد» می‌خورد. مقصد از روی نقش انتخاب می‌شود.
        let roleId: number | null = null;
        const fromToken = getRoleId(access_token);
        if (typeof fromToken === "number") {
          roleId = fromToken;
        } else {
          // اگر role_id در payload توکن نبود، از خود سرور می‌پرسیم.
          try {
            const me = await axios.get(
              "https://portal-api.mahanpanel.com/auth/me",
              { headers: { Authorization: `Bearer ${access_token}` } }
            );
            if (typeof me.data?.role_id === "number") roleId = me.data.role_id;
          } catch (meError) {
            console.error("Error resolving role after login:", meError);
          }
        }
        const landing = getLandingRoute(roleId);
        setTimeout(() => navigate(landing), 1800);
      } catch (error: any) {
        if (error.response?.status === 401) {
          toast.error('نام کاربری یا رمز عبور اشتباه است');
        } else if (error.response?.status === 429) {
          toast.error('تعداد درخواست‌ها بیش از حد مجاز است، لطفاً چند دقیقه صبر کنید');
        } else {
          toast.error('خطا در ارتباط با سرور');
        }
        console.error('Login error:', error);
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <div className="flex w-full shadow-lg flex-col items-center justify-center p-4 sm:p-10">
      <div className="w-full max-w-md space-y-6">
        {/* هدر فرم */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg">
            <LogIn className="h-8 w-8 text-white" />
          </div>
          <h2
            className="text-2xl font-bold text-gray-800"
            style={{ fontFamily: "Vazir, sans-serif" }}
          >
            ورود به حساب کاربری
          </h2>
          <p
            className="mt-1 text-sm text-gray-500"
            style={{ fontFamily: "Vazir, sans-serif" }}
          >
            خوش آمدید! لطفاً اطلاعات خود را وارد کنید
          </p>
        </div>

        {/* فرم */}
        <form onSubmit={formik.handleSubmit} className="space-y-5">
          <div className="space-y-1">
            <label
              className="block text-sm font-medium text-gray-700"
              style={{ fontFamily: "Vazir, sans-serif" }}
            >
              نام کاربری یا ایمیل
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <User className="h-5 w-5 text-[#B4B4B4]" />
              </div>
              <input
                type="text"
                name="username"
                autoComplete="off"
                dir="rtl"
                className="block w-full rounded-xl border border-[#B4B4B4] bg-gray-50/50 py-3 pr-11 pl-4 text-gray-800 placeholder:text-[#B4B4B4] focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                placeholder="نام کاربری یا ایمیل خود را وارد کنید"
                style={{ fontFamily: "Vazir, sans-serif" }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.username}
              />
            </div>
            {formik.touched.username && formik.errors.username && (
              <p
                className="mt-1 text-xs text-red-500"
                style={{ fontFamily: "Vazir, sans-serif" }}
              >
                {formik.errors.username}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              className="block text-sm font-medium text-gray-700"
              style={{ fontFamily: "Vazir, sans-serif" }}
            >
              رمز عبور
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <Lock className="h-5 w-5 text-[#B4B4B4]" />
              </div>
              <input
                type={passwordVisible ? "text" : "password"}
                name="password"
                autoComplete="off"
                dir="rtl"
                className="block w-full rounded-xl border border-[#B4B4B4] bg-gray-50/50 py-3 pr-11 pl-12 text-gray-800 placeholder:text-[#B4B4B4] focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                placeholder="رمز عبور خود را وارد کنید"
                style={{ fontFamily: "Vazir, sans-serif" }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
              />
              <button
                type="button"
                onClick={() => setPasswordVisible(!passwordVisible)}
                className="absolute inset-y-0 left-3 flex items-center text-[#B4B4B4] hover:text-gray-600"
              >
                {passwordVisible ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p
                className="mt-1 text-xs text-red-500"
                style={{ fontFamily: "Vazir, sans-serif" }}
              >
                {formik.errors.password}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label
              className="flex cursor-pointer items-center gap-2 text-sm text-gray-600"
              style={{ fontFamily: "Vazir, sans-serif" }}
            >
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-[#B4B4B4] text-red-600 focus:ring-red-300"
              />
              <span>مرا به خاطر بسپار</span>
            </label>
            <button
              type="button"
              onClick={() =>
                toast.info("جهت بازیابی رمز عبور با پشتیبانی تماس بگیرید")
              }
              className="text-sm font-medium text-blue-500 hover:text-blue-600"
              style={{ fontFamily: "Vazir, sans-serif" }}
            >
              رمز خود را فراموش کرده‌اید؟
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="group relative w-full overflow-hidden rounded-xl bg-red-500 py-3 font-bold text-white shadow-md transition-all hover:scale-[1.02] hover:shadow-red-300/50 disabled:opacity-70"
            style={{ fontFamily: "Vazir, sans-serif" }}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>در حال ورود...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <LogIn className="h-5 w-5" />
                <span>ورود به ماهان پانل</span>
              </div>
            )}
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#B4B4B4]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span
                className="rounded-full bg-white px-3 py-0.5 text-xs text-[#B4B4B4]"
                style={{ fontFamily: "Vazir, sans-serif" }}
              >
                یا از طریق
              </span>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              type="button"
              className="rounded-full bg-gray-100 p-2.5 text-gray-600 transition hover:bg-red-100 hover:text-red-600"
              onClick={() => toast.info("ادغام با گوگل در حال توسعه")}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </button>
            <button
              type="button"
              className="rounded-full bg-gray-100 p-2.5 text-gray-600 transition hover:bg-red-100 hover:text-red-600"
              onClick={() => toast.info("ادغام با گیت‌هاب در حال توسعه")}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.5.09.68-.22.68-.48 0-.24-.01-.88-.01-1.73-2.78.6-3.37-1.2-3.37-1.2-.45-1.15-1.11-1.46-1.11-1.46-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02.8-.22 1.65-.33 2.5-.33.85 0 1.7.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85 0 1.34-.01 2.42-.01 2.75 0 .27.18.58.69.48C19.13 20.17 22 16.42 22 12c0-5.52-4.48-10-10-10z" />
              </svg>
            </button>
          </div>
        </form>
      </div>

      <Modal
        show={showWelcome}
        onHide={() => setShowWelcome(false)}
        dialogClassName="w-screen md:w-[26rem]"
      >
        <Modal.Body className="flex flex-col items-center gap-4 p-10 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg shadow-red-500/30">
            <PartyPopper className="h-8 w-8 text-white" />
          </div>
          <h3
            className="text-xl font-bold text-slate-800 dark:text-white"
            style={{ fontFamily: "Vazir, sans-serif" }}
          >
            خوش آمدید!
          </h3>
          <p
            className="text-sm text-slate-500 dark:text-zink-200"
            style={{ fontFamily: "Vazir, sans-serif" }}
          >
            ورود شما با موفقیت انجام شد، در حال انتقال به پانل هستید...
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// -------- کامپوننت اصلی صفحه لاگین --------
const LoginPage: React.FC = () => {
  return (
    <div dir="rtl" className="min-h-screen overflow-hidden">
      <FontLoader />
      <div className="flex min-h-screen flex-col lg:flex-row">
        {/* فرم لاگین - در موبایل کل فضا را گرفته و وسط می‌آید */}
        <div className="flex w-full flex-1 items-center justify-center bg-white lg:w-1/2">
          <div className="w-full max-w-md p-4 sm:p-6">
            <LoginForm />
          </div>
        </div>
        {/* برندینگ - در موبایل مخفی می‌شود */}
        <BrandSide />

      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        rtl
        theme="colored"
      />
    </div>
  );
};

export default LoginPage;
